import { useEffect, type ReactElement } from 'react';
import { ActionIcon, Box, Group, Loader, Text, Tooltip } from '@mantine/core';
import { Pencil, Pin, Trash2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { SurfaceCard } from '@/design/SurfaceCard';
import { useViewModel } from '@/shared/viewmodels/useViewModel';
import type { AnalyticsField } from '@/shared/analytics/AnalyticsTypes';
import { chartPlanFromTileSpec, type PinnedTile } from './PinnedTileTypes';
import { PinnedTileViewModel } from './PinnedTileViewModel';
import { sliceToPath } from './TopicSliceKey';
import { useAssistFilters } from './assist/AssistSurfaceProvider';
import SurfaceAssistChart from './assist/SurfaceAssistChart';

interface PinnedTileCardProps {
    tile: PinnedTile;
    basePath: string;
    onEdit: () => void;
    onRemove: () => void;
}

/**
 * A user-pinned visualization rendered as a gallery tile. Self-loads its data
 * (composing the tile's filters with the page's live filters), re-queries when
 * either changes, and renders through the shared assist chart so a pinned tile
 * looks identical to its AMA origin. Clicking a bar/slice drills into the
 * evidence for that value (a separate page scoped by the slice) rather than
 * filtering the current page.
 */
export default function PinnedTileCard({ tile, basePath, onEdit, onRemove }: PinnedTileCardProps): ReactElement {
    const vm = useViewModel(PinnedTileViewModel);
    const navigate = useNavigate();
    const filters = useAssistFilters();

    useEffect(() => {
        void vm.load(tile.spec, filters);
    }, [vm, tile.spec, filters]);

    const plan = chartPlanFromTileSpec(tile.spec);
    const handleDrill = (_field: AnalyticsField, value: string): void => {
        navigate(sliceToPath(basePath, tile.id, { primary: value }));
    };

    return (
        <SurfaceCard $padding="md" style={{ height: '100%' }}>
            <Group justify="space-between" align="flex-start" mb="sm" wrap="nowrap">
                <Group gap={8} wrap="nowrap" style={{ minWidth: 0 }}>
                    <Pin size={14} style={{ color: 'var(--mantine-primary-color-filled)', flexShrink: 0 }} />
                    <Text fw={700} truncate style={{ fontSize: '14px', letterSpacing: '-0.02em' }}>
                        {tile.spec.title}
                    </Text>
                </Group>
                <Group gap={2} wrap="nowrap" style={{ flexShrink: 0 }}>
                    <Tooltip label="Edit tile" withArrow>
                        <ActionIcon variant="subtle" color="gray" size="sm" aria-label="Edit tile" onClick={onEdit}>
                            <Pencil size={14} />
                        </ActionIcon>
                    </Tooltip>
                    <Tooltip label="Remove tile" withArrow>
                        <ActionIcon variant="subtle" color="gray" size="sm" aria-label="Remove tile" onClick={onRemove}>
                            <Trash2 size={14} />
                        </ActionIcon>
                    </Tooltip>
                </Group>
            </Group>
            {vm.loading && vm.aggregate === null && vm.trend === null ?
                <Group justify="center" py="lg">
                    <Loader size="sm" />
                </Group>
            : vm.error !== null ?
                <Text size="sm" c="red">
                    {vm.error}
                </Text>
            :   <Box>
                    <SurfaceAssistChart plan={plan} aggregate={vm.aggregate} trend={vm.trend} onDrill={handleDrill} />
                </Box>
            }
        </SurfaceCard>
    );
}
